import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";

const MEASUREMENT_COLORS = {
  acc: "#8B5CF6",          // Violet
  hr: "#E11D48",           // Ruby Red
  temperature: "#EA580C",  // Warm Orange
  eda: "#10B981",          // Emerald Green
  spo2estim: "#16C2CF",    // Cyan (Brand Secondary)
  calories: "#F97316",     // Orange
  event: "#4F46E5",        // Indigo
  total_steps: "#104581"   // Deep Blue (Brand Primary)
};

// Pure global patch, independent of props/state; do it once at module load
// instead of reassigning the same function on every render.
Highcharts.Point.prototype.highlight = function () {
  this.onMouseOver();
  this.series.chart.tooltip.refresh(this);
  this.series.chart.xAxis[0].drawCrosshair(null, this);
};

function syncTooltip(containerId, pointIndex) {
  Highcharts.charts.forEach((chart) => {
    if (chart) {
      const point = chart.series[0].points[pointIndex];
      if (point) {
        if (chart.container.id === containerId) {
          chart.tooltip.refresh(point);
          chart.xAxis[0].drawCrosshair(null, point);
        } else {
          point.highlight();
        }
      }
    }
  });
}

const getChartOptions = (
  measurement,
  showXAxis,
  colorIndex,
  isLastChart,
  seriesName,
  commonXAxisCategories,
) => {
  const yValues = measurement.data.map((point) => point.y);

  return {
  accessibility: {
    enabled: false,
  },
  chart: {
    type: "area",
    height: isLastChart ? 180 : 75,
    events: {
      load() {
        const chart = this;
        chart.container.onmousemove = function (e) {
          const event = chart.pointer.normalize(e);
          const pointIndex = chart.xAxis[0].toValue(event.chartX, true);
          syncTooltip(chart.container.id, Math.round(pointIndex));
        };
      },
    },
  },
  navigator: {
    enabled: isLastChart,
    xAxis: {
      labels: {
        enabled: true,
        formatter: function () {
          return commonXAxisCategories[this.value];
        },
      },
    },
    adaptToUpdatedData: false,
    series: {
      data: yValues,
    },
  },

  rangeSelector: {
    enabled: false,
  },
  scrollbar: {
    enabled: isLastChart,
  },
  title: {
    text: null,
  },
  legend: {
    enabled: false,
  },
  xAxis: {
    categories: commonXAxisCategories,
    crosshair: true,
    labels: {
      enabled: showXAxis,
    },
    events: {
      setExtremes: function (e) {
        if (e.triggered !== "syncExtremes") {
          Highcharts.charts.forEach(function (chart) {
            if (chart !== this) {
              chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
                triggered: "syncExtremes",
              });
            }
          });
        }
      },
    },
  },
  yAxis: {
    title: {
      text: measurement.name,
    },
  },
  series: [
    {
      name: seriesName,
      data: yValues,
      color: MEASUREMENT_COLORS[measurement.key] || "#104581",
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [
            0,
            Highcharts.color(MEASUREMENT_COLORS[measurement.key] || "#104581")
              .setOpacity(0.3)
              .get("rgba"),
          ],
          [
            1,
            Highcharts.color(MEASUREMENT_COLORS[measurement.key] || "#104581")
              .setOpacity(0.1)
              .get("rgba"),
          ],
        ],
      },
      lineWidth: 2,
      states: {
        hover: {
          enabled: true,
          lineWidth: 2,
        },
      },
      marker: {
        enabled: false,
      },
    },
  ],
  tooltip: {
    shared: true,
    crosshairs: true,
    valueDecimals: 2,
  },
  credits: {
    enabled: false,
  },
  responsive: {
    rules: [
      {
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          legend: {
            enabled: false,
          },
          yAxis: {
            labels: {
              enabled: false,
            },
          },
          xAxis: {
            labels: {
              enabled: false,
            },
          },
          chart: {
            height: 200,
          },
        },
      },
    ],
  },
  };
};

const SessionGraphCharts = ({ chartsData, commonXAxisCategories, chartRefs, lastVisibleIndex }) => (
  <div className="graph_charts">
    {chartsData.map((chartData, index) => {
      if (!chartData.hasData) {
        return (
          <div key={chartData.key} className="graph_chart_empty">
            <span className="graph_chart_empty_label">
              {chartData.name}
            </span>
            <span>No data recorded for this session</span>
          </div>
        );
      }
      const isLastChart = index === lastVisibleIndex;
      return (
        <div
          key={chartData.key}
          className="graph_chart"
          id={`chart-container-${index}`}
        >
          <HighchartsReact
            highcharts={Highcharts}
            containerProps={{ id: `highcharts-${index}` }}
            options={getChartOptions(
              chartData,
              isLastChart,
              index,
              isLastChart,
              chartData.name,
              commonXAxisCategories,
            )}
            ref={(el) => (chartRefs.current[index] = el && el.chart)}
          />
        </div>
      );
    })}
  </div>
);

export default SessionGraphCharts;
